import type { ReactNode } from 'react';

/**
 * A column heading. A plain string is aligned by what it names; pass the object
 * form to say so explicitly.
 */
export type Heading = string | { label: ReactNode; align: 'left' | 'right' | 'center' };

/**
 * Words that name a number.
 *
 * Amounts and weights are right-aligned in the body so their decimal points
 * line up — that is what makes a column of money readable at a glance. Their
 * headings were left-aligned, so every numeric column had its title sitting
 * over the wrong end. Rather than tag twenty tables by hand and have the next
 * one forget, a heading that names a number is aligned like one.
 */
const NUMERIC = [
  'amount',
  'total',
  'qty',
  'quantity',
  'pieces',
  'count',
  'rate',
  'cost',
  'price',
  'value',
  'wt',
  'weight',
  'grams',
  'gms',
  'balance',
  'debit',
  'credit',
  'profit',
  'margin',
  'tax',
  'gst',
  'cgst',
  'sgst',
  'igst',
  'taxable',
  'discount',
  'paid',
  'outstanding',
  'due',
  'received',
  'refund',
  'bonus',
  'instalments',
  'fine',
  'net',
  'gross',
  'making',
  'wastage',
  '%',
  'per gram',
  'per fine gram',
  'days',
  // Counts and differences: a stock count, a melt, a register.
  'expected',
  'counted',
  'found',
  'not found',
  'missing',
  'variance',
  'difference',
  'short',
  'excess',
  'loss',
  'charges',
  'lines',
  'items',
  'melts',
  'bars',
  'fee',
];

export function headingAlign(h: Heading): 'left' | 'right' | 'center' {
  if (typeof h !== 'string') return h.align;
  const t = h.toLowerCase().trim();
  if (t === '') return 'left';
  // Purity and location are chosen from a list, not typed as figures, so they
  // stay left with the rest of the words.
  return NUMERIC.some((w) => t === w || t.endsWith(` ${w}`) || t.startsWith(`${w} `) || t.includes(`${w} (`))
    ? 'right'
    : 'left';
}

export function headingLabel(h: Heading): ReactNode {
  return typeof h === 'string' ? h : h.label;
}

export const ALIGN_CLASS: Record<'left' | 'right' | 'center', string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};
